import { inject, injectable } from 'inversify';
import Debug from 'debug';
import { TrackingService2, TrackingService2Symbol, createCommand } from '@/app/tracking/api';
import { ExamResultStoreModule, ExamResultStoreModuleSymbol, ExamStoreModuleSymbol, ExamStoreModule } from '../../state';
import { CompleteExamTrackingCommand } from '../../tracking/api';
import { isExamPassed } from '../../domain/exam-calculations';
import { CompleteCourseCommand, CompleteCourseCommandPayload } from '@/app/course/tracking/api';
import { CourseCompletionServiceSymbol, CourseCompletionService } from '@/app/course-completion/service';

const debug = Debug('finish-exam-command')

export interface FinishExamCommandPayload {
  id: string,
  score: Score,
  durationSeconds: number
}

export interface Score {
  min: number
  max: number
  raw: number
}

@injectable()
export class FinishExamAttemptCommandHandler {

  constructor(
    @inject(ExamResultStoreModuleSymbol)
    private examResultStore: ExamResultStoreModule,
    @inject(ExamStoreModuleSymbol)
    private examStoreModule: ExamStoreModule,
    @inject(CourseCompletionServiceSymbol)
    private courseCompletionService: CourseCompletionService,
    @inject(TrackingService2Symbol)
    private trackingService: TrackingService2) {
    
  }

  execute({ id, score, durationSeconds }: FinishExamCommandPayload) {
    debug('finish exam', id, score)

    const findResult = this.examStoreModule.findExam(id)
    if (!findResult.success) {
      throw new Error(`Coding error: exam not found with id ${id}`)
    }

    const exam = findResult.value
    debug('found exam', exam)

    const { passingScore, isPassed } = isExamPassed(score, exam.percentToPass)

    const result = (isPassed ? 'passed' : 'failed') as 'passed' | 'failed'
    const timestamp = new Date()
    const attempt = {
      timestamp: timestamp,
      score: {
        min: score.min,
        max: score.max,
        raw: score.raw
      },
      passingScore: passingScore,
      durationSeconds: durationSeconds,
      result: result
    }

    debug('storing new attempt', attempt, exam.gradingMethod)

    this.examResultStore.addAttempt({id, attempt, gradingMethod: exam.gradingMethod})

    this.trackingService.execute(createCommand<CompleteExamTrackingCommand>('complete-exam', {
      id: id,
      attempt: {
        timestamp: timestamp,
        score: {
          min: score.min,
          max: score.max,
          raw: score.raw
        },
        passingScore: passingScore,
        result: result,
        durationSeconds: durationSeconds
      },
      effectiveGrading: exam.gradingMethod,
      meta: {
        title: exam.title,
        role: exam.role
      }
    }))

    const effectiveResult = this.examResultStore.findExam(id)
    debug('effective result', effectiveResult)

    if (effectiveResult === false) {
      throw new Error('Coding error: missing exam: ' + id)
    }

    if (exam.role === 'final' && effectiveResult.result === 'passed') {
      debug('final exam, send result', effectiveResult)
      this.courseCompletionService.reportPassedFinalExam({
        min: effectiveResult.score.min,
        max: effectiveResult.score.max,
        raw: effectiveResult.score.raw
      })
    }
  }
}
